/**
 * One level of the tag-provider folder tree, fetched dynamically per
 * navigation step (instead of loading every tag up front). Returns:
 *
 *   - `directTags`: tags whose `folder` equals exactly the requested
 *     `$currentPath`.
 *   - `directUdtInstances`: `:UDTInstance` nodes anchored at exactly
 *     that `$currentPath` (rendered as folder rows in the explorer —
 *     clicking one transitions to UDT-instance navigation via
 *     `PLANT_UDT_FOLDER_NODE_CYPHER`).
 *   - `subfolders`: the first-segment subfolder names below this
 *     level, each with a recursive count of leaf entries (both `:Tag`
 *     nodes and `:UDTMember` leaves reachable through `:UDTInstance`s
 *     rooted in that subtree).
 *
 * Three `CALL { ... }` subqueries isolate the direct-tag fetch, the
 * direct-UDT-instance fetch, and the subfolder aggregation so none can
 * swallow the others. The subfolder math:
 *   1. Union folder-bearing entries from BOTH `:Tag.folder` (each
 *      counted as 1 leaf) and `:UDTInstance.folder_name` (each counted
 *      by its direct `:UDTMember` leaf count, treating nested
 *      `member_type='udt_instance'` members as 1 — an approximation
 *      that doesn't recurse into nested-instance definitions, but good
 *      enough for the badge).
 *   2. Filter entries strictly below `$currentPath`
 *      (`STARTS WITH ($currentPath + '/')`, or any non-empty folder
 *      when at root).
 *   3. Strip the prefix to get the path relative to here.
 *   4. Take the first slash-separated segment — that's the subfolder
 *      name at this level.
 *   5. Group by that segment, sum the weights.
 *
 * `:UDTInstance` is reached via the denormalized `provider_name`
 * property (there's no `PROVIDES` edge to UDT instances by design —
 * see `udt-mapper.ts`).
 *
 * Built as a function so the `matchUdtDefByTypeId` fragment is
 * interpolated at call time with caller-chosen aliases.
 *
 * Params: `{ providerId: string; currentPath: string }`.
 */
import { matchUdtDefByTypeId } from './udt-def-fragment'

export const PLANT_PROVIDER_FOLDER_NODE_CYPHER = `
            MATCH (tp:TagProvider {id: $providerId})

            CALL {
              WITH tp
              OPTIONAL MATCH (tp)-[:PROVIDES]->(t:Tag)
                WHERE coalesce(t.folder, t.folder_name, '') = $currentPath
                  AND t.tag_path IS NOT NULL
                  AND trim(t.tag_path) <> ''
              WITH t
              ORDER BY toLower(coalesce(t.name, ''))
              RETURN collect(t) AS directTags
            }

            CALL {
              WITH tp
              OPTIONAL MATCH (ui:UDTInstance)
                WHERE ui.provider_name = tp.name
                  AND coalesce(ui.folder_name, '') = $currentPath
              ${matchUdtDefByTypeId('def', 'ui.type_id')}
              OPTIONAL MATCH (def)-[:HAS_MEMBER]->(leafM:UDTMember)
                WHERE coalesce(leafM.member_type, '') <> 'udt_instance'
              WITH ui, def, count(distinct leafM) AS memberCount
              WHERE ui IS NOT NULL
              ORDER BY toLower(coalesce(ui.instance_name, ui.name, ''))
              RETURN collect({
                instance: ui,
                definition: def,
                memberCount: memberCount
              }) AS directUdtInstances
            }

            CALL {
              WITH tp
              CALL {
                WITH tp
                OPTIONAL MATCH (tp)-[:PROVIDES]->(t:Tag)
                WITH coalesce(t.folder, t.folder_name, '') AS folderPath, t
                WHERE t IS NOT NULL AND t.tag_path IS NOT NULL AND trim(t.tag_path) <> ''
                RETURN collect({path: folderPath, w: 1}) AS tagFolders
              }
              CALL {
                WITH tp
                OPTIONAL MATCH (ui:UDTInstance)
                  WHERE ui.provider_name = tp.name
                ${matchUdtDefByTypeId('idef', 'ui.type_id')}
                OPTIONAL MATCH (idef)-[:HAS_MEMBER]->(leafM:UDTMember)
                  WHERE coalesce(leafM.member_type, '') <> 'udt_instance'
                WITH ui, count(leafM) AS w
                WHERE ui IS NOT NULL
                RETURN collect({
                  path: coalesce(ui.folder_name, ''),
                  w: CASE WHEN w = 0 THEN 1 ELSE w END
                }) AS udtFolders
              }
              UNWIND (tagFolders + udtFolders) AS entry
              WITH entry.path AS folderPath, entry.w AS w
              WHERE folderPath <> ''
                AND folderPath <> $currentPath
                AND ($currentPath = '' OR folderPath STARTS WITH ($currentPath + '/'))
              WITH CASE
                     WHEN $currentPath = '' THEN folderPath
                     ELSE substring(folderPath, size($currentPath) + 1)
                   END AS rest, w
              WITH split(rest, '/')[0] AS firstSegment, sum(w) AS tagCount
              WHERE firstSegment IS NOT NULL AND firstSegment <> ''
              WITH firstSegment, tagCount
              ORDER BY toLower(firstSegment)
              RETURN collect({name: firstSegment, tagCount: tagCount}) AS subfolders
            }

            RETURN directTags, directUdtInstances, subfolders
            LIMIT 10000
          `
